/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import CampaignModel from 'cio/models/campaign';
import { Channel, Measure, Periods } from 'cio/utils/metrics/common';
import { JourneyMetricsMeasure } from 'cio/utils/journey-metrics';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { inject as service } from '@ember/service';
import MetricsService from 'cio/services/metrics';
import moment from 'moment-timezone';
import merge from 'lodash.merge';
import { addDashedZonesToIncompleteDataPoints, mapPercentDataPoints } from 'cio/utils/graphs';
import { Grouping, groupTimestamps } from 'cio/utils/metrics/resolution';
import type { Options, TooltipFormatterCallbackFunction, TooltipFormatterContextObject } from 'highcharts';
// @ts-ignore ember-svg-jar merges the helpers with the app, so we can import it
// from the cio/ directory structure. ts-ignore because we don't have type
// definitions for that
import { svgJar } from 'cio/helpers/svg-jar';
import { pct } from 'cio/helpers/pct';
import { formatNumber } from 'cio/helpers/format-number';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import { chartChangeData, ChartSeriesItem, DataContainer, SidebarItem } from 'cio/utils/campaign-overview/metrics';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import { guidFor } from '@ember/object/internals';
import { ActionType } from 'cio/models/action';
import { measureLabel } from 'cio/helpers/measure-label';

interface BaseCampaignMetricsPageChartCardSectionArgs {
  campaign: CampaignModel;
  timeRange: DateRange;
  timePeriod: Periods;
  grouping: Grouping;
  unit: 'count' | 'percentage';
  isLoading: boolean;
  sidebarItems: SidebarItem<Measure | JourneyMetricsMeasure>[];
  selectedSidebarValues: string[];
  onSelectSidebarItem: (id: string, checked: boolean) => unknown;
  timezone: string;
  tooltipItemLabel: string;
  graphTypeOverrides?: Record<JourneyMetricsMeasure | Measure | string, 'line' | 'column'>;
  defaultGraphType?: string;
  measureType: MeasureType;
  onTrackHover?: (time: number) => unknown;
  channel?: Channel;
}

interface AggregateCampaignMetricsPageChartCardSectionArgs extends BaseCampaignMetricsPageChartCardSectionArgs {
  aggregateData: DataContainer<JourneyMetricsMeasure | Measure>;
  chartType: 'aggregate';
}

interface IndividualCampaignMetricsPageChartCardSectionArgs extends BaseCampaignMetricsPageChartCardSectionArgs {
  individualData: Record<string, DataContainer<Measure | JourneyMetricsMeasure>>;
  chartType: 'individual';
  individualMeasures: { label: string; value: Measure }[];
  individualMeasure: Measure;
  onIndividualMeasureChange: (value: Measure) => unknown;
}

type CampaignMetricsPageChartCardSectionArgs =
  | AggregateCampaignMetricsPageChartCardSectionArgs
  | IndividualCampaignMetricsPageChartCardSectionArgs;

const CHART_HEIGHT = {
  aggregate: 418,
  individual: 378,
};

const BASE_CHART_OPTIONS: Options = {
  chart: {
    height: CHART_HEIGHT.aggregate,
  },
  legend: {
    enabled: false,
  },
  time: {
    timezone: 'Etc/UTC',
  },
  xAxis: {
    type: 'datetime',
    startOfWeek: 1,
  },
  plotOptions: {
    series: {
      zoneAxis: 'x',
      zones: [],
    },
  },
};

export default class CampaignMetricsPageChartCardSection extends Component<CampaignMetricsPageChartCardSectionArgs> {
  @service metrics!: MetricsService;

  get sectionId() {
    return guidFor(this);
  }

  get defaultGraphType() {
    return this.args.defaultGraphType ?? 'column';
  }

  get chartType() {
    return this.args.chartType ?? 'aggregate';
  }

  get selectedSidebarOptions() {
    return this.args.sidebarItems.filter((i) => this.args.selectedSidebarValues.includes(i.id));
  }

  get sidebarItemIds() {
    return this.args.sidebarItems.map((i) => i.id);
  }

  @tracked chartOptions: Options = BASE_CHART_OPTIONS;

  @action
  setChartOptions() {
    let timezoneRange = {
      start: moment.tz(`${this.args.timeRange.start?.format('YYYY-MM-DD')} 00:00:00`, this.args.timezone),
      end: moment.tz(`${this.args.timeRange.end?.format('YYYY-MM-DD')} 00:00:00`, this.args.timezone),
    };

    let isRate = this.args.unit === 'percentage';

    let timestamps = groupTimestamps(this.args.timeRange, this.args.grouping);

    let { tooltipItemLabel, measureType } = this.args;
    let { sidebarItemIds } = this;

    let series: ChartSeriesItem[] = [...(this.selectedSidebarOptions ?? [])]
      .sort((a, b) => {
        return sidebarItemIds.indexOf(a.id) - sidebarItemIds.indexOf(b.id);
      })
      .map((m) => {
        let measure = this.args.chartType === 'aggregate' ? m.measure : this.args.individualMeasure;

        let rateData =
          this.args.chartType === 'aggregate'
            ? this.args.aggregateData?.rateSeries?.[measure] ?? []
            : this.args.individualData[m.id]?.rateSeries?.[measure] ?? [];
        let totalData =
          this.args.chartType === 'aggregate'
            ? this.args.aggregateData?.totalSeries?.[measure] ?? []
            : this.args.individualData[m.id]?.totalSeries?.[measure] ?? [];

        let denominator = '';

        if (this.args.measureType === MeasureType.JourneyMetrics) {
          denominator = findDenominator(measure as JourneyMetricsMeasure, MeasureType.JourneyMetrics);
        } else if (this.args.measureType === MeasureType.DeliveryMetrics && this.args.channel != null) {
          denominator = findDenominator(
            measure as Measure,
            MeasureType.DeliveryMetrics,
            `${this.args.channel}_action` as ActionType,
          );
        }

        return {
          id: m.id,
          index: m.index,
          name: m.label,
          data: isRate && m.hasRate ? mapPercentDataPoints(rateData) : totalData,
          yAxis: isRate && !m.hasRate ? 1 : 0,
          custom: {
            rateData,
            totalData,
            rateChangeData: chartChangeData(rateData, m.measure),
            totalChangeData: chartChangeData(totalData, m.measure),
            colorHexValue: m.color.value,
            hasRate: m.hasRate,
            measureType,
            denominator,
          },
          colorIndex: m.color.name,
          type: this.args.graphTypeOverrides?.[m.id] ?? this.defaultGraphType,
        };
      });

    let options: Options = merge({}, BASE_CHART_OPTIONS, {
      chart: {
        height: CHART_HEIGHT[this.chartType],
      },
      series: series as any,
    });

    options.yAxis = [
      {
        labels: {
          format: isRate ? '{value:.1f}%' : undefined,
        },
      },
      {
        opposite: false,
      },
    ];

    let changeOverLabel = {
      [Grouping.Hourly]: 'hour',
      [Grouping.Daily]: 'day',
      [Grouping.Weekly]: 'week',
      [Grouping.Monthly]: 'month',
    }[this.args.grouping];

    // We use a very customized tooltip to provide more detailed data within.
    // Unfortunately there is no way to embed Ember components as a Highcharts
    // tooltip - we can only define a function that returns an HTML string to be
    // rendered, which is what this formatter does
    options.tooltip = {
      outside: true,
      formatter: function (
        this: TooltipFormatterContextObject,
        tooltip: any, // this one should ideally be the Tooltip highcharts type,
        // but it seems their type definitions aren't quite complete
        callback: any,
      ) {
        let firstPointConfig = this.points?.[0]?.point.getLabelConfig();
        let header = firstPointConfig ? tooltip.tooltipFooterHeaderFormatter(firstPointConfig) : '';
        return `
<div class="fly-popover">
  <div class="fly-popover__content px-1.5 pt-1 pb-0.5 w-[400px] max-w-[none]">
    <table class="table-fixed">
      <thead>
        <tr>
          <th class="text-sm text-left py-0.5 pr-1.5">${header}</th>
          <th class="font-normal text-sm text-gray-500 text-right py-0.5 w-[120px]">change over ${changeOverLabel}</th>
        </tr>
      </thead>

      <tbody class="divide-y divide-gray-200">
        ${[...(this.points ?? [])]
          .sort((a, b) => {
            let aId = (a.series as any).userOptions.id;
            let bId = (b.series as any).userOptions.id;

            return sidebarItemIds.indexOf(aId) - sidebarItemIds.indexOf(bId);
          })
          .map((p) => {
            let { index } = p.point;
            let isNull = p.point.options.custom?.isNull ?? false;

            let customOptions = p.series.options.custom;

            let measureType = customOptions?.measureType;
            let denominator = customOptions?.denominator;

            let hasRateData = customOptions?.rateData[index] != null;
            let rateValue = hasRateData ? customOptions?.rateData[index].y : 0;
            let totalValue = customOptions?.totalData[index].y;
            let changeData =
              isRate && customOptions?.hasRate
                ? customOptions?.rateChangeData[index]
                : customOptions?.totalChangeData[index];

            return `
          <tr>
            <td class="py-1">
              <div class="flex text-sm w-full">
                <div class="mr-1 flex items-center">
                  <span class="fly-color-swatch m-0" style="background-color: ${customOptions?.colorHexValue};"></span>
                </div>
                <div class="overflow-hidden overflow-ellipsis">
                  ${p.series.name}
                </div>
                <div class="mr-0.5">:</div>
                  ${
                    hasRateData
                      ? `
                      <div class="font-medium mr-0.5">
                        ${pct([rateValue])}
                      </div>`
                      : ''
                  }
                  ${
                    hasRateData && isNull
                      ? `<div class="text-muted">0 ${
                          measureType === MeasureType.JourneyMetrics ? 'journeys' : 'messages'
                        } were ${(measureLabel([denominator, measureType]) ?? '').toLowerCase()}</div>`
                      : `
                      <div>
                        ${formatNumberAndPluralize([totalValue, tooltipItemLabel])}
                      </div>`
                  }
              </div>
            </td>
            <td class="text-sm pl-1.5">
              <div class="flex justify-end">
                <div>
                  ${
                    index > 0 && changeData?.changeFromLast?.trend != null
                      ? svgJar(`trend-${changeData?.changeFromLast?.trend}`, {
                          class: `block ${changeData?.trendIconClass}`,
                          stroke: 'none',
                          width: '16px',
                          height: '16px',
                        })
                      : ''
                  }
                </div>
                <div class="ml-1.5 ${changeData?.trendTextClass ?? ''}">
                  ${
                    index > 0
                      ? `${changeData?.changeFromLast?.sign ?? ''}${
                          isRate && customOptions?.hasRate
                            ? pct([changeData?.changeFromLast?.change])
                            : formatNumber([changeData?.changeFromLast?.change])
                        }
                  `
                      : '—'
                  }
                </div>
              </div>
            </td>
          </tr>`;
          })
          .join('')}
      </tbody>
    </table>
    ${callback ? callback(tooltip) : ''}
  </div>
</div>
    `;
      } as TooltipFormatterCallbackFunction,
    };

    if (this.args.grouping === Grouping.Weekly) {
      (options.xAxis as any).startOfWeek = timezoneRange.start.day();
    }
    options = addDashedZonesToIncompleteDataPoints(options, timestamps, timezoneRange, this.args.grouping);

    options.time = {
      timezone: this.args.timezone,
    };

    this.chartOptions = options;
  }

  @action
  onTrackHover(time: number) {
    return this.args.onTrackHover?.(time);
  }
}
